import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import MessagePreviewDialog from '../shared/MessagePreviewDialog';
import SmsSegmentCounter from '../shared/SmsSegmentCounter';
import {
  CARD_SX,
  Caption,
  FOCUS_RING_SX,
  KIT_DIALOG_ACTIONS_SX,
  KIT_DIALOG_TITLE_SX,
  KitHeader,
  Overline,
  SEGMENT_SX,
  StepTitle,
  TONE,
  TagChip,
  optionChipSx,
} from '../shared/designKit';
import { codeFont, numFont } from '../../utils/fonts';
import { calculateSmsSegments } from '../../utils/smsSegments';
import { channelIcon, channelShortTranslationKey, formatCost, triggerLabel } from './reminderUtils';
import ReminderPreviewDialog from './ReminderPreviewDialog';
import PublicReminderOptOutPage from './PublicReminderOptOutPage';

const EMPTY_FORM = {
  name: '',
  trigger_key: 'oil_change',
  interval_months: 12,
  channel: 'email_else_sms',
  send_hour: 10,
  send_weekdays_only: true,
  sms_body: '',
  email_subject: '',
  email_body: '',
};

const CHANNELS = ['email_else_sms', 'email', 'sms'];

const intervalValid = (months) => months !== '' && months >= 3 && months <= 120;

// Create/edit dialog. Same component for both, keyed off the nullable `rule`
// prop. Explicit Save/Cancel per house rules; activation is NOT here — new rules
// start inactive and are activated from the list after the dry-run preview.
//
// Laid out like Tarjousautomaatti: three numbered steps on the left (trigger and interval,
// channel and timing, messages), a grey summary panel on the right that restates the rule
// as the customer will experience it, actions along the bottom.
export default function ReminderRuleDialog({ open, onClose, onSave, rule, catalog }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentOrganization } = useUserStore();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [messagePreviewOpen, setMessagePreviewOpen] = useState(false);

  // What the customer gets with the texts in the dialog, saved or not.
  const loadMessagePreview = async (language) => {
    const res = await axios.post(
      '/api/reminder-rules/message-preview/',
      { ...form, language },
      { params: { organization: currentOrganization?.organization_id } },
    );
    return res.data;
  };
  const renderOptOutPage = (page, { language }) => (
    <PublicReminderOptOutPage previewData={page.data} previewLanguage={language} />
  );

  useEffect(() => {
    if (open) {
      if (rule) {
        setForm({
          name: rule.name || '',
          trigger_key: rule.trigger_key,
          interval_months: rule.interval_months,
          channel: rule.channel,
          send_hour: rule.send_hour ?? 10,
          send_weekdays_only: rule.send_weekdays_only ?? true,
          sms_body: rule.sms_body || '',
          email_subject: rule.email_subject || '',
          email_body: rule.email_body || '',
        });
      } else {
        // New rule: seed from the default trigger's template
        const trigger = catalog?.triggers?.find((item) => item.key === EMPTY_FORM.trigger_key);
        setForm({
          ...EMPTY_FORM,
          name: trigger ? `${trigger.label_fi} ${trigger.default_months} kk` : '',
          interval_months: trigger?.default_months ?? 12,
          sms_body: trigger?.default_sms_fi || '',
          email_subject: trigger?.default_email_subject_fi || '',
          email_body: trigger?.default_email_fi || '',
        });
      }
    }
  }, [open, rule, catalog]);

  const triggers = catalog?.triggers || [];
  const selectedTrigger = triggers.find((item) => item.key === form.trigger_key);
  const selectedTriggerLabel = triggerLabel(catalog, form.trigger_key, i18n.language);

  const handleTriggerChange = (key) => {
    const trigger = triggers.find((item) => item.key === key);
    setForm((prev) => {
      const next = { ...prev, trigger_key: key };
      if (!rule && trigger) {
        // Creating: re-seed defaults for the newly picked trigger
        next.name = `${trigger.label_fi} ${trigger.default_months} kk`;
        next.interval_months = trigger.default_months;
        next.sms_body = trigger.default_sms_fi || '';
        next.email_subject = trigger.default_email_subject_fi || '';
        next.email_body = trigger.default_email_fi || '';
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const success = await onSave(form, rule?.id);
      if (success) {
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const needsSms = form.channel === 'sms' || form.channel === 'email_else_sms';
  const needsEmail = form.channel === 'email' || form.channel === 'email_else_sms';

  const smsFrame = catalog?.sms_frame || { prefix: '', suffix: '' };
  const smsPrice = catalog?.sms_price_per_segment ? Number(catalog.sms_price_per_segment) : null;
  const bookingUrl = catalog?.booking_url || '';
  // Count segments with the real booking URL substituted — the placeholder token is
  // much shorter than the URL it becomes (incl. the ?rekisteri= prefill), and the
  // workshop pays per real segment.
  const smsBodyForCount = form.sms_body
    .split('{{varauslinkki}}')
    .join(catalog?.booking_url_sample || bookingUrl);
  const assembledSms = smsBodyForCount ? `${smsFrame.prefix}${smsBodyForCount}${smsFrame.suffix}` : '';
  const smsSegments = assembledSms ? calculateSmsSegments(assembledSms).segmentCount : 0;

  const step1Done = !!form.trigger_key && !!form.name.trim() && intervalValid(form.interval_months);
  const step3Done = (!needsSms || !!form.sms_body.trim()) && (!needsEmail || (!!form.email_subject.trim() && !!form.email_body.trim()));
  const canSave = form.name.trim() && intervalValid(form.interval_months);
  const canPreview = !!form.trigger_key && !!form.interval_months;
  const sendTime = `${String(form.send_hour).padStart(2, '0')}:00`;

  const textField = (props) => (
    <TextField size="small" fullWidth {...props} />
  );

  const triggerChip = (trigger) => {
    const selected = trigger.key === form.trigger_key;
    const chip = (
      <Chip
        key={trigger.key}
        label={triggerLabel(catalog, trigger.key, i18n.language)}
        clickable
        color={selected ? 'primary' : 'default'}
        variant={selected ? 'filled' : 'outlined'}
        icon={selected ? <CheckIcon sx={{ fontSize: 15 }} /> : trigger.advanced ? <WarningAmberOutlinedIcon sx={{ fontSize: 15 }} /> : undefined}
        onClick={() => handleTriggerChange(trigger.key)}
        data-testid={`rule-trigger-${trigger.key}`}
        sx={{
          ...optionChipSx(selected),
          ...(trigger.advanced && !selected ? { '& .MuiChip-icon': { color: TONE.amber.color, ml: '8px', mr: '-4px' } } : {}),
        }}
      />
    );
    if (!trigger.advanced || selected) return chip;
    return (
      <Tooltip key={trigger.key} title={t('service_reminders.trigger_advanced_warning')} arrow enterTouchDelay={0}>
        {chip}
      </Tooltip>
    );
  };

  const triggerStep = (
    <Box component="section">
      <StepTitle number={1} title={t('service_reminders.step_trigger')} done={step1Done} />
      <Overline sx={{ mb: 1 }}>{t('service_reminders.trigger')}</Overline>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }} data-testid="rule-trigger-options">
        {triggers.map(triggerChip)}
      </Box>
      {selectedTrigger?.advanced && (
        <Caption icon={WarningAmberOutlinedIcon} tone={TONE.amber.color} sx={{ mt: 1.25 }}>
          {t('service_reminders.trigger_advanced_warning')}
        </Caption>
      )}
      {selectedTrigger?.visit_based_notice && (
        <Caption testId="rule-trigger-visit-based-notice" sx={{ mt: 1.25 }}>
          {t('service_reminders.trigger_visit_based_notice')}
        </Caption>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2.25, flexWrap: 'wrap' }}>
        {textField({
          label: t('service_reminders.rule_name'),
          value: form.name,
          onChange: (e) => setForm({ ...form, name: e.target.value }),
          inputProps: { maxLength: 255, 'data-testid': 'rule-name-input' },
          sx: { flex: '1 1 220px' },
        })}
        {textField({
          label: t('service_reminders.interval_label'),
          type: 'number',
          value: form.interval_months,
          onChange: (e) => {
            const val = parseInt(e.target.value, 10);
            setForm({ ...form, interval_months: Number.isNaN(val) ? '' : val });
          },
          inputProps: { min: 3, max: 120, 'data-testid': 'rule-interval-input', style: { fontWeight: 600 } },
          InputProps: { endAdornment: <InputAdornment position="end">{t('service_reminders.months_unit')}</InputAdornment>, sx: { ...numFont } },
          error: form.interval_months !== '' && !intervalValid(form.interval_months),
          sx: { flex: '0 1 160px', minWidth: 140 },
        })}
      </Box>
      <Caption sx={{ mt: 1 }}>{t('service_reminders.interval_months_help')}</Caption>
    </Box>
  );

  const deliveryStep = (
    <Box component="section">
      <StepTitle number={2} title={t('service_reminders.step_delivery')} done />
      <Overline sx={{ mb: 1 }}>{t('service_reminders.channel')}</Overline>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={form.channel}
        onChange={(event, value) => value && setForm({ ...form, channel: value })}
        sx={SEGMENT_SX}
        data-testid="rule-channel-options"
      >
        {CHANNELS.map((channel) => (
          <ToggleButton key={channel} value={channel} data-testid={`rule-channel-${channel}`}>
            {channelIcon(channel)}
            {t(channelShortTranslationKey(channel))}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Caption sx={{ mt: 1 }}>{t(`service_reminders.channel_help_${form.channel}`)}</Caption>
      {needsSms && <Caption sx={{ mt: 0.5, pl: '23px' }} icon={null}>{t('service_reminders.sms_billing_note')}</Caption>}

      <Box sx={{ display: 'flex', gap: 2, mt: 2.25, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ width: 150 }}>
          <InputLabel>{t('service_reminders.send_hour')}</InputLabel>
          <Select
            value={form.send_hour}
            label={t('service_reminders.send_hour')}
            onChange={(e) => setForm({ ...form, send_hour: e.target.value })}
            sx={{ ...numFont, fontWeight: 600 }}
          >
            {Array.from({ length: 24 }, (_, h) => (
              <MenuItem key={h} value={h} sx={{ ...numFont }}>{`${String(h).padStart(2, '0')}:00`}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={form.send_weekdays_only}
              onChange={(e) => setForm({ ...form, send_weekdays_only: e.target.checked })}
            />
          }
          label={<Typography variant="body2">{t('service_reminders.weekdays_only')}</Typography>}
          sx={{ ml: 0 }}
        />
      </Box>
    </Box>
  );

  const placeholders = catalog?.placeholders || [];
  const messagesStep = (
    <Box component="section">
      <StepTitle number={3} title={t('service_reminders.step_messages')} done={step3Done} />
      <Overline sx={{ mb: 1 }}>{t('service_reminders.placeholders_title')}</Overline>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {placeholders.map((token) => (
          <TagChip key={token} label={token} tone={TONE.slate} sx={{ ...codeFont, fontWeight: 500 }} />
        ))}
      </Box>
      <Caption sx={{ mt: 1 }}>{t('service_reminders.placeholders_help')}</Caption>
      {bookingUrl && (
        <Caption sx={{ mt: 0.5, pl: '23px' }} icon={null}>
          {t('service_reminders.booking_link_hint', { token: '{{varauslinkki}}', url: bookingUrl })}
        </Caption>
      )}

      {needsSms && (
        <Box sx={{ mt: 2.5 }}>
          {textField({
            label: t('service_reminders.sms_body'),
            multiline: true,
            rows: 3,
            value: form.sms_body,
            onChange: (e) => setForm({ ...form, sms_body: e.target.value }),
            inputProps: { 'data-testid': 'rule-sms-body' },
          })}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
            <Caption>{t('service_reminders.sms_body_help')}</Caption>
            <Box sx={{ pl: '23px', '& > *': { mt: 0 } }}>
              <SmsSegmentCounter
                prefix={smsFrame.prefix}
                suffix={smsFrame.suffix}
                text={smsBodyForCount}
                defaultMessage=""
              />
            </Box>
          </Box>
        </Box>
      )}

      {needsEmail && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2.5 }}>
          {textField({
            label: t('service_reminders.email_subject'),
            value: form.email_subject,
            onChange: (e) => setForm({ ...form, email_subject: e.target.value }),
            inputProps: { maxLength: 255 },
          })}
          <Box>
            {textField({
              label: t('service_reminders.email_body'),
              multiline: true,
              rows: 7,
              value: form.email_body,
              onChange: (e) => setForm({ ...form, email_body: e.target.value }),
            })}
            <Caption sx={{ mt: 0.75 }}>{t('service_reminders.email_body_help')}</Caption>
          </Box>
        </Box>
      )}
    </Box>
  );

  const summaryRow = (label, value, { first = false, testId } = {}) => (
    <Box sx={{ px: 2, py: 1.25, borderTop: first ? 0 : '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }}>{label}</Typography>
      <Typography
        variant="body2"
        component="div"
        data-testid={testId}
        sx={{ fontWeight: 600, mt: 0.375, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', lineHeight: 1.4 }}
      >
        {value}
      </Typography>
    </Box>
  );

  const smsSummary = !needsSms
    ? t('service_reminders.summary_no_sms')
    : smsSegments > 0
      ? [
        t('smsSegments.count', { count: smsSegments }),
        smsPrice !== null ? t('service_reminders.summary_sms_per_message', { cost: formatCost(smsPrice * smsSegments) }) : null,
      ].filter(Boolean).join(' · ')
      : '…';

  const summary = (
    <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2.25, md: 2.75 }, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Overline sx={{ mb: 1 }}>{t('service_reminders.summary_title')}</Overline>
        <Typography
          data-testid="rule-summary-interval"
          sx={{ fontSize: { xs: 34, md: 40 }, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1, whiteSpace: 'nowrap', ...numFont }}
        >
          {intervalValid(form.interval_months) ? t('service_reminders.months_value', { months: form.interval_months }) : '…'}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1.25, lineHeight: 1.45 }}>
          {t('service_reminders.summary_interval_line', { trigger: selectedTriggerLabel })}
        </Typography>
      </Box>

      <Box sx={{ ...CARD_SX, boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
        {summaryRow(t('service_reminders.channel'), (
          <>
            {channelIcon(form.channel, { fontSize: 16, color: 'text.secondary' })}
            {t(channelShortTranslationKey(form.channel))}
          </>
        ), { first: true })}
        {summaryRow(t('service_reminders.send_hour'), (
          <Box component="span" sx={{ ...numFont }}>
            {t('service_reminders.summary_send_time', { time: sendTime })}
            {' · '}
            {t(form.send_weekdays_only ? 'service_reminders.summary_weekdays' : 'service_reminders.summary_any_day')}
          </Box>
        ))}
        {summaryRow(t('service_reminders.summary_sms'), (
          <Box component="span" sx={{ ...numFont, color: needsSms ? 'text.primary' : 'text.secondary', fontWeight: needsSms ? 600 : 500 }}>
            {smsSummary}
          </Box>
        ), { testId: 'rule-summary-sms' })}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {!rule && <Caption>{t('service_reminders.new_rule_inactive_note')}</Caption>}
        {needsSms && <Caption icon={null} sx={{ pl: '23px' }}>{t('service_reminders.sms_body_help')}</Caption>}
        {needsEmail && !needsSms && <Caption icon={null} sx={{ pl: '23px' }}>{t('service_reminders.email_body_help')}</Caption>}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={fullScreen}
        data-testid="reminder-rule-dialog"
        sx={FOCUS_RING_SX}
      >
        <DialogTitle sx={KIT_DIALOG_TITLE_SX}>
          <KitHeader
            icon={<EventRepeatOutlinedIcon />}
            title={rule ? t('service_reminders.edit_rule') : t('service_reminders.new_rule')}
            subtitle={t('service_reminders.rules_intro')}
            onClose={onClose}
          />
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 3fr) minmax(0, 2fr)' }, minHeight: '100%' }}>
            <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2.5, md: 3.25 }, display: 'flex', flexDirection: 'column', gap: { xs: 3.25, md: 3.75 }, minWidth: 0 }}>
              {triggerStep}
              {deliveryStep}
              {messagesStep}
            </Box>
            <Box
              sx={(theme_) => ({
                bgcolor: 'background.default',
                [theme_.breakpoints.up('md')]: { borderLeft: `1px solid ${theme_.palette.divider}` },
                [theme_.breakpoints.down('md')]: { borderTop: `1px solid ${theme_.palette.divider}` },
              })}
            >
              <Box sx={{ position: { md: 'sticky' }, top: 0 }}>{summary}</Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={KIT_DIALOG_ACTIONS_SX}>
          <Button
            variant="outlined"
            startIcon={<GroupsOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={!canPreview}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('service_reminders.preview_button')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setMessagePreviewOpen(true)}
            disabled={!canPreview}
            data-testid="rule-message-preview-button"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('service_reminders.message_preview_button')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>{t('Cancel')}</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={saving || !canSave}
            data-testid="rule-save-button"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', minWidth: 110 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <MessagePreviewDialog
        open={messagePreviewOpen}
        onClose={() => setMessagePreviewOpen(false)}
        title={t('service_reminders.message_preview_title')}
        pageLabel={t('service_reminders.message_preview_page_tab')}
        defaultLanguage={currentOrganization?.default_language}
        loadPreview={loadMessagePreview}
        renderPage={renderOptOutPage}
      />

      {previewOpen && (
        <ReminderPreviewDialog
          open
          onClose={() => setPreviewOpen(false)}
          rule={{ ...form, id: rule?.id }}
          catalog={catalog}
          activateAfter={false}
        />
      )}
    </>
  );
}
